import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type {SxProps} from '@mui/system';
import {lighten} from '@mui/system/colorManipulator';
import {type ChangeEvent, type FC, useEffect, useState} from 'react';
import {primaryColor, secondaryColor} from '@/theme/theme';

//TYPES
type Props = {
  editable: boolean;
  value: string;
  onSave: (value: string) => void;
};

const InputTextEditableOnHover: FC<Props> = ({editable, value, onSave}) => {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [newValue, setNewValue] = useState(value);

  useEffect(() => {
    setNewValue(value);
  }, [value]);

  const handleMouseEnter = () => editable && setHover(true);
  const handleMouseLeave = () => editable && setHover(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setNewValue(e.target.value);

  const handleBlur = () => {
    setEditing(false);
    if (newValue !== value) {
      onSave(newValue);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={styles(hover, editing).container}
    >
      <TextField
        disabled={!editable}
        value={newValue}
        placeholder="-"
        type="text"
        variant="standard"
        onClick={() => setEditing(true)}
        onChange={handleChange}
        onBlur={handleBlur}
        size="small"
        sx={styles(hover, editing).input}
      />
      <EditOutlinedIcon color="primary" sx={styles(hover, editing).icon} />
    </Stack>
  );
};
export default InputTextEditableOnHover;

const styles = (hover: boolean, editing: boolean): Record<string, SxProps> => ({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    border: editing || hover ? '1px solid #d3d3d3' : '1px solid transparent',
    backgroundColor: editing || hover ? lighten(secondaryColor, 0.9) : 'white',
    transition: 'all 500ms ease-in-out',
    pr: 0.5,
    width: '100%',
  },
  input: {
    pt: 0.5,
    pl: '4px',
    width: '100%',
    height: '34px',
    '& .MuiInput-root': {
      fontWeight: 900,
      '&::before': {
        borderBottom: editing ? `1px solid ${primaryColor}` : '1px solid transparent',
      },
      '&::after': {
        borderBottom: editing ? '2px solid' : '1px solid transparent',
      },
      '&:hover:not(.Mui-disabled)::before': {
        borderBottom: editing ? `1px solid ${primaryColor}` : '1px solid transparent',
      },
    },
  },
  icon: {
    opacity: hover ? 1 : 0,
    transition: 'opacity 500ms ease-in-out',
  },
});
